/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.model.dashboard.DashboardSummary

class DashboardRepositoryImpl(private val supabase: SupabaseClient) : DashboardRepository {

    private val snapshots = MutableStateFlow<Map<String, Result<DashboardSummary>>>(emptyMap())
    private var lastRequestedMonth: String? = null

    override fun getDashboard(month: String): Flow<Result<DashboardSummary>> =
        snapshots
            .onStart {
                val cached = snapshots.value[month]
                if (cached == null || cached.isFailure) fetch(month)
            }
            .map { it[month] }
            .filterNotNull()

    override suspend fun refresh(): Result<Unit> = safeCall {
        lastRequestedMonth?.let { fetch(it) }
    }

    private suspend fun fetch(month: String) {
        lastRequestedMonth = month
        val result = safeCall {
            supabase.postgrest.rpc(
                "get_dashboard",
                buildJsonObject { put("target_month", month) },
            ).decodeAs<DashboardSummary>()
        }
        snapshots.value = snapshots.value + (month to result)
    }
}
